import React, { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { getCitiesByState } from '../../data/locations/cities';
import { getAreasForCity } from '../../data/locations/areas';
import type { StateValue } from '../../data/locations/states';
import { capitalizeWords } from '../../utils/stringUtils';

interface StateLocationSelectorProps {
  state: string;
  onCityChange: (city: string) => void;
  onAreaChange: (area: string) => void;
  selectedBrand?: string;
}

export default function StateLocationSelector({ 
  state, 
  onCityChange, 
  onAreaChange,
  selectedBrand 
}: StateLocationSelectorProps) {
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [availableAreas, setAvailableAreas] = useState<Array<{ value: string; label: string; pincode?: string }>>([]);

  // Get cities for the current state
  const stateCities = getCitiesByState(state as StateValue);
  const formattedState = capitalizeWords(state.replace(/-/g, ' '));

  useEffect(() => {
    // Reset selections when state changes
    setSelectedCity('');
    setSelectedArea('');
    onCityChange('');
    onAreaChange('');
  }, [state, onCityChange, onAreaChange]);

  useEffect(() => {
    if (selectedCity) {
      const areas = getAreasForCity(selectedCity);
      setAvailableAreas(areas);
    } else {
      setAvailableAreas([]);
    }
  }, [selectedCity]);

  const handleCityChange = (city: string) => {
    setSelectedCity(city);
    setSelectedArea('');
    onCityChange(city);
    onAreaChange('');
  };

  const handleAreaChange = (area: string) => {
    setSelectedArea(area);
    onAreaChange(area);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
      <div className="space-y-4">
        {/* City Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <MapPin className="inline-block w-4 h-4 mr-2" />
            Select City in {formattedState}
          </label>
          <select
            value={selectedCity}
            onChange={(e) => handleCityChange(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="">Select City</option>
            {stateCities.map((city) => (
              <option key={city.value} value={city.value}>
                {city.label}
              </option>
            ))}
          </select>
        </div>

        {/* Area Selection */}
        {selectedCity && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="inline-block w-4 h-4 mr-2" />
              Select Area
            </label>
            <select
              value={selectedArea}
              onChange={(e) => handleAreaChange(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select Area</option>
              {availableAreas.map((area) => (
                <option key={area.value} value={area.value}>
                  {area.label} {area.pincode ? `(${area.pincode})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}